import "./TaskModal.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import React, { useCallback, useEffect, useMemo } from "react";
import { setShowSendToOpsModal } from "reducers/main";
import { useCreateTaskMutation } from "services/task-api";
import { useDispatch, useSelector } from "utils/redux";

import KaboodleModal from "components/Modals/KaboodleModal";

import TaskForm from "./TaskForm";
import {
  applyApiFieldErrors,
  createSendToOpsDefaultValues,
  toNumericSelectValue,
  toTaskDueDatePayload,
} from "../../utils/taskModal.helpers";
import type { TaskFormData } from "../../types/taskModal.types";
import { useTaskForm } from "../../hooks/useTaskForm";
import { GENERIC_TASK_TYPE_CODE } from "../../utils/task-type-utils";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

/* submit lightweight generic tasks directly to operations users. */
const SendToOpsModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const constants = useSelector((state) => state.shared.constants);

  const formMethods = useTaskForm({
    defaultValues: createSendToOpsDefaultValues(),
  });

  const {
    control,
    reset,
    handleSubmit,
    setError,
    formState: { errors },
  } = formMethods;

  const [createTask, { error, isSuccess }] = useCreateTaskMutation();

  useEffect(() => {
    applyApiFieldErrors<TaskFormData>({
      error,
      setError,
    });
  }, [error, setError]);

  useEffect(() => {
    if (isSuccess) {
      reset(createSendToOpsDefaultValues());
      KaboodleToaster.success("Task successfully sent to ops");
      onClose();
    }
  }, [isSuccess, onClose, reset]);

  const onSubmit = async (data: TaskFormData) => {
    const formattedData = {
      message: data.message,
      assignee: toNumericSelectValue(data.assignee),
      type: GENERIC_TASK_TYPE_CODE,
      due_date: toTaskDueDatePayload(data.due_date),
      file: null,
      service: null,
    };

    try {
      await createTask({ data: formattedData }).unwrap();
    } catch (submitError) {
      console.error("Failed to create task:", submitError);
      const handled = applyApiFieldErrors<TaskFormData>({
        error: submitError,
        setError,
      });

      if (!handled) {
        KaboodleToaster.error("Failed to send task to ops");
      }
    }
  };

  const handleClose = useCallback(() => {
    reset(createSendToOpsDefaultValues());
    dispatch(setShowSendToOpsModal(false));
    onClose();
  }, [dispatch, onClose, reset]);

  const footerContent = useMemo(() => {
    return <Button onClick={handleSubmit(onSubmit)}>Send To Ops</Button>;
  }, [handleSubmit, onSubmit]);

  const opsUsers = useMemo(() => {
    return (constants?.USERS ?? []).filter((user) =>
      user.roles.includes("Operations"),
    );
  }, [constants]);

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose} footer={footerContent}>
      <Card heading="Send To Ops">
        <TaskForm
          control={control}
          errors={errors}
          assigneeOptions={opsUsers}
          assigneeLabel="Ops Team Member"
          messageMode="plain"
          messageRequired={true}
        />
      </Card>
    </KaboodleModal>
  );
};

export default SendToOpsModal;
